import React, { useEffect, useMemo, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import { ChartLayout, EquityContent, MethodologyContent, OverviewContent, RelevanceContent } from './ChartLayout';
import { KeyMetric, KeyMetrics } from './KeyMetrics';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { formatFilename } from '../../utils/downloadUtils';
import {
  buildLeadBurdenAuthoredBundle,
  leadBurdenOverviewDescription,
  LEAD_BURDEN_DEFINITION,
} from '../../domains/narrative/leadEnergyBurdenNarrative';
import { AuthoredEquityPanel, AuthoredRelevancePanel } from '../../domains/narrative/narrativeSectionContent';
import { resolveMethodologyContentProps } from '../../domains/narrative/methodologyContentProps';

type ViewMode = 'incomeBands' | 'frameworks';
type NarrativeView = 'overview' | 'relevance' | 'equity' | 'methodology';

type EnergyBurdenRow = {
  framework: string;
  geofips: string | number;
  location_name: string | null;
  geography_type: string;
  year: number;
  income_band: string;
  energy_burden_pct_income: number | null;
  total_income: number | null;
  total_energy_cost: number | null;
  units: number | null;
};

interface LeadEnergyCostBurdenSectionProps {
  location: string;
  geofips?: string | number | null;
}

const TABLE = 'lead_energy_cost_burden_all_frameworks';
const DEFAULT_YEAR = 2022;
const FRAMEWORK_ORDER = ['AMI', 'FPL', 'LLSI', 'SMI'];
const INCOME_BAND_ORDER = ['0-30%', '30-60%', '60-80%', '80-100%', '100-150%', '150%+'];

function normalizeGeofips(value: unknown): string | null {
  if (value == null) return null;
  const s = String(value).trim();
  if (!s) return null;
  return s.replace(/^0+/, '') || '0';
}

function geofipsVariants(geofips: string | number | null | undefined): string[] {
  const normalized = normalizeGeofips(geofips);
  if (!normalized) return [];
  const out = new Set<string>([normalized]);
  if (/^\d+$/.test(normalized)) {
    if (normalized.length === 6) out.add(`0${normalized}`);
    if (normalized.length === 7) out.add(normalized.slice(1));
  }
  return Array.from(out);
}

function percentileRank(values: number[], target: number): number | null {
  if (!values.length) return null;
  if (values.length === 1) return 50;
  const sorted = [...values].sort((a, b) => a - b);
  const first = sorted.indexOf(target);
  const last = sorted.lastIndexOf(target);
  if (first < 0) return null;
  const avgRank = (first + last) / 2;
  return (avgRank / (sorted.length - 1)) * 100;
}

function formatPct(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${value.toFixed(2)}%`;
}

const LeadEnergyCostBurdenSection: React.FC<LeadEnergyCostBurdenSectionProps> = ({ location, geofips }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  const [activeView, setActiveView] = useState<NarrativeView>('overview');
  const [viewMode, setViewMode] = useState<ViewMode>('incomeBands');
  const [selectedFramework, setSelectedFramework] = useState<string>('AMI');
  const [selectedIncomeBand, setSelectedIncomeBand] = useState<string>('0-30%');
  const [rows, setRows] = useState<EnergyBurdenRow[]>([]);
  const [resolvedGeographyType, setResolvedGeographyType] = useState<'place' | 'county'>('place');
  const [peerPercentile, setPeerPercentile] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const availableFrameworks = useMemo(() => {
    const present = new Set(rows.map((r) => r.framework));
    return FRAMEWORK_ORDER.filter((f) => present.has(f));
  }, [rows]);

  const availableIncomeBands = useMemo(() => {
    const present = new Set(rows.map((r) => r.income_band));
    return INCOME_BAND_ORDER.filter((b) => present.has(b));
  }, [rows]);

  const selectedMetric = useMemo(() => {
    const hit = rows.find((r) => r.framework === selectedFramework && r.income_band === selectedIncomeBand);
    return hit?.energy_burden_pct_income ?? null;
  }, [rows, selectedFramework, selectedIncomeBand]);

  const selectedRow = useMemo(
    () => rows.find((r) => r.framework === selectedFramework && r.income_band === selectedIncomeBand),
    [rows, selectedFramework, selectedIncomeBand]
  );

  const leadAuthoredBundle = useMemo(() => {
    if (!selectedRow) return null;
    return buildLeadBurdenAuthoredBundle({
      locationName: location,
      year: selectedRow.year ?? DEFAULT_YEAR,
      framework: selectedFramework,
      incomeBand: selectedIncomeBand,
      burdenPct: selectedRow.energy_burden_pct_income,
      totalEnergyCost: selectedRow.total_energy_cost,
      totalIncome: selectedRow.total_income,
      householdUnits: selectedRow.units,
      peerPercentile,
    });
  }, [location, selectedRow, selectedFramework, selectedIncomeBand, peerPercentile]);

  const highestBandForFramework = useMemo(() => {
    const items = rows
      .filter((r) => r.framework === selectedFramework && r.energy_burden_pct_income != null)
      .map((r) => ({ incomeBand: r.income_band, value: Number(r.energy_burden_pct_income) }));
    if (!items.length) return null;
    return items.reduce((a, b) => (a.value >= b.value ? a : b));
  }, [rows, selectedFramework]);

  useEffect(() => {
    let cancelled = false;
    const loadRows = async () => {
      try {
        setLoading(true);
        setError(null);
        await supabaseLocationService.initialize();

        const locationType = supabaseLocationService.getLocationType(location);
        const preferredGeoTypes: Array<'place' | 'county'> =
          locationType === 'county' ? ['county'] : ['place', 'county'];
        const geoAliases: Record<'place' | 'county', string[]> = {
          place: ['place', 'Place', 'city', 'City'],
          county: ['county', 'County'],
        };

        const variants = geofipsVariants(geofips);
        let found: EnergyBurdenRow[] = [];
        let foundType: 'place' | 'county' = 'place';

        for (const gt of preferredGeoTypes) {
          for (const v of variants) {
            const { data, error: qErr } = await supabaseClient
              .from(TABLE)
              .select(
                'framework, geofips, location_name, geography_type, year, income_band, energy_burden_pct_income, total_income, total_energy_cost, units'
              )
              .eq('year', DEFAULT_YEAR)
              .in('geography_type', geoAliases[gt])
              .eq('geofips', v);
            if (qErr) continue;
            if (data && data.length > 0) {
              found = data as EnergyBurdenRow[];
              foundType = gt;
              break;
            }
          }
          if (found.length > 0) break;
        }

        if (!found.length) {
          for (const gt of preferredGeoTypes) {
            const { data, error: qErr } = await supabaseClient
              .from(TABLE)
              .select(
                'framework, geofips, location_name, geography_type, year, income_band, energy_burden_pct_income, total_income, total_energy_cost, units'
              )
              .eq('year', DEFAULT_YEAR)
              .in('geography_type', geoAliases[gt])
              .ilike('location_name', `${location}%`)
              .limit(80);
            if (qErr) continue;
            if (data && data.length > 0) {
              found = data as EnergyBurdenRow[];
              foundType = gt;
              break;
            }
          }
        }

        if (!cancelled) {
          setRows(found);
          setResolvedGeographyType(foundType);
          if (!found.length) setError('No Energy Cost Burden data found for this location.');
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load Energy Cost Burden data.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadRows();
    return () => {
      cancelled = true;
    };
  }, [location, geofips]);

  useEffect(() => {
    if (availableFrameworks.length && !availableFrameworks.includes(selectedFramework)) {
      setSelectedFramework(availableFrameworks[0]);
    }
  }, [availableFrameworks, selectedFramework]);

  useEffect(() => {
    if (availableIncomeBands.length && !availableIncomeBands.includes(selectedIncomeBand)) {
      setSelectedIncomeBand(availableIncomeBands[0]);
    }
  }, [availableIncomeBands, selectedIncomeBand]);

  useEffect(() => {
    let cancelled = false;
    const loadPeerPercentile = async () => {
      if (!selectedFramework || !selectedIncomeBand || selectedMetric == null || !rows.length) {
        setPeerPercentile(null);
        return;
      }
      const { data, error: qErr } = await supabaseClient
        .from(TABLE)
        .select('energy_burden_pct_income')
        .eq('year', DEFAULT_YEAR)
        .in(
          'geography_type',
          resolvedGeographyType === 'place' ? ['place', 'Place', 'city', 'City'] : ['county', 'County']
        )
        .eq('framework', selectedFramework)
        .eq('income_band', selectedIncomeBand)
        .not('energy_burden_pct_income', 'is', null);
      if (cancelled || qErr || !data) return;
      const peers = data
        .map((d: { energy_burden_pct_income: number | null }) => d.energy_burden_pct_income)
        .filter((v: number | null): v is number => v != null && Number.isFinite(v));
      const p = percentileRank(peers, selectedMetric);
      if (!cancelled) setPeerPercentile(p);
    };
    loadPeerPercentile();
    return () => {
      cancelled = true;
    };
  }, [rows, selectedFramework, selectedIncomeBand, selectedMetric, resolvedGeographyType]);

  useEffect(() => {
    if (!chartRef.current) return;

    if (chartInstance.current) {
      chartInstance.current.destroy();
      chartInstance.current = null;
    }

    if (!rows.length) return;

    const makeIncomeBandChart = () => {
      const labels = availableIncomeBands;
      const values = labels.map((band) => {
        const hit = rows.find((r) => r.framework === selectedFramework && r.income_band === band);
        return hit?.energy_burden_pct_income ?? null;
      });
      return {
        labels,
        datasets: [
          {
            label: `${selectedFramework} energy burden`,
            data: values,
            backgroundColor: '#2563eb',
            borderRadius: 4,
          },
        ],
      };
    };

    const makeFrameworkChart = () => {
      const labels = availableFrameworks;
      const values = labels.map((framework) => {
        const hit = rows.find((r) => r.framework === framework && r.income_band === selectedIncomeBand);
        return hit?.energy_burden_pct_income ?? null;
      });
      return {
        labels,
        datasets: [
          {
            label: `${selectedIncomeBand} burden`,
            data: values,
            backgroundColor: '#0ea5e9',
            borderRadius: 4,
          },
        ],
      };
    };

    const data = viewMode === 'incomeBands' ? makeIncomeBandChart() : makeFrameworkChart();

    chartInstance.current = new Chart(chartRef.current, {
      type: 'bar',
      data,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'bottom' },
          title: {
            display: true,
            text:
              viewMode === 'incomeBands'
                ? `${selectedFramework}: Energy Cost Burden by Income Band (${DEFAULT_YEAR})`
                : `Framework Comparison at ${selectedIncomeBand} (${DEFAULT_YEAR})`,
          },
          tooltip: {
            callbacks: {
              label: (ctx) => `${ctx.dataset.label}: ${formatPct(Number(ctx.parsed.y))}`,
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            title: { display: true, text: 'Energy burden (% of income)' },
            ticks: { callback: (v) => `${v}%` },
          },
        },
      },
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [rows, viewMode, selectedFramework, selectedIncomeBand, availableIncomeBands, availableFrameworks]);

  const rowsForTable = useMemo(
    () =>
      rows
        .slice()
        .sort((a, b) => {
          const fa = FRAMEWORK_ORDER.indexOf(a.framework);
          const fb = FRAMEWORK_ORDER.indexOf(b.framework);
          if (fa !== fb) return fa - fb;
          return INCOME_BAND_ORDER.indexOf(a.income_band) - INCOME_BAND_ORDER.indexOf(b.income_band);
        }),
    [rows]
  );

  return (
    <ChartLayout
      scrollAnchorId="chart-energy-cost-burden"
      title="Energy Cost Burden by Income Band"
      description="This chart shows energy cost burden (% of household income spent on electricity and fuels) for the selected location using 2022 LEAD data."
      chartType="bar"
      activeView={activeView}
      onViewChange={(v) => setActiveView(v as NarrativeView)}
      showRelevanceEquity={true}
      enableComments={true}
      location={location}
      keyMetrics={
        <KeyMetrics>
          <KeyMetric
            value={selectedMetric != null ? formatPct(selectedMetric) : '—'}
            label={`${selectedFramework} ${selectedIncomeBand} burden`}
          />
          <KeyMetric
            value={highestBandForFramework ? `${highestBandForFramework.incomeBand} (${formatPct(highestBandForFramework.value)})` : '—'}
            label={`${selectedFramework} highest burden band`}
          />
          <KeyMetric
            value={peerPercentile != null ? `${peerPercentile.toFixed(1)}th` : '—'}
            label={`${resolvedGeographyType} peer percentile`}
          />
        </KeyMetrics>
      }
      chartSelector={{
        options: [
          { value: 'incomeBands', label: 'Income Bands' },
          { value: 'frameworks', label: 'Framework Comparison' },
        ],
        value: viewMode,
        onChange: (v) => setViewMode(v as ViewMode),
      }}
      additionalButtonGroups={[
        {
          label: 'Framework',
          options: availableFrameworks.map((f) => ({ value: f, label: f })),
          value: selectedFramework,
          onChange: setSelectedFramework,
        },
        {
          label: 'Income Band',
          options: availableIncomeBands.map((b) => ({ value: b, label: b })),
          value: selectedIncomeBand,
          onChange: setSelectedIncomeBand,
        },
      ]}
      chartContent={
        <div style={{ minHeight: 420 }}>
          {loading ? (
            <p>Loading Energy Cost Burden data...</p>
          ) : error ? (
            <p style={{ color: '#b91c1c' }}>{error}</p>
          ) : (
            <>
              <div style={{ height: 340 }}>
                <canvas ref={chartRef} />
              </div>
              <div style={{ marginTop: 12, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: '6px 8px' }}>Framework</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: '6px 8px' }}>Income Band</th>
                      <th style={{ textAlign: 'right', borderBottom: '1px solid #e5e7eb', padding: '6px 8px' }}>Burden (%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rowsForTable.map((r) => (
                      <tr key={`${r.framework}-${r.income_band}`}>
                        <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6' }}>{r.framework}</td>
                        <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6' }}>{r.income_band}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', borderBottom: '1px solid #f3f4f6' }}>
                          {formatPct(r.energy_burden_pct_income)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      }
      overviewContent={
        <OverviewContent
          title="Overview"
          description={leadBurdenOverviewDescription(
            `Energy burden in ${location} uses modeled LEAD household energy cost and income for ${resolvedGeographyType === 'place' ? 'place' : 'county'} geography.`
          )}
        >
          <p>{LEAD_BURDEN_DEFINITION}</p>
          {leadAuthoredBundle?.keyInsights ? <p>{leadAuthoredBundle.keyInsights}</p> : null}
        </OverviewContent>
      }
      relevanceContent={
        leadAuthoredBundle?.relevance ? (
          <AuthoredRelevancePanel bundle={leadAuthoredBundle} />
        ) : (
        <RelevanceContent
          title="Relevance"
          description="Energy cost burden directly measures affordability pressure and helps identify where households are most exposed to utility and fuel cost stress."
        >
          <div className="methodology-details-simple">
            <h5>Policy Targeting</h5>
            <p>
              The chart supports targeting for bill assistance, weatherization and building electrification incentives by showing
              where burden is highest by income segment.
            </p>
          </div>
        </RelevanceContent>
        )
      }
      equityContent={
        leadAuthoredBundle?.equity ? (
          <AuthoredEquityPanel bundle={leadAuthoredBundle} />
        ) : (
          <EquityContent
            title="Equity"
            description="Energy burden is an affordability equity indicator because lower-income households often face higher cost shares even when total energy use is lower."
          >
            <div className="methodology-details-simple">
              <h5>Income-Sensitive View</h5>
              <p>
                Comparing across income bands and frameworks helps avoid overgeneralizing citywide averages and reveals where
                affordability stress is concentrated.
              </p>
            </div>
          </EquityContent>
        )
      }
      methodologyContent={
        <MethodologyContent
          {...resolveMethodologyContentProps(leadAuthoredBundle?.methodology, {
            title: 'Methodology',
            description:
              'Source: LEAD 2022 tract data, aggregated to place/county using relationship_trct_place_20 population weights.',
            dataSources: ['DOE LEAD 2022', 'relationship_trct_place_20.csv'],
            methodology: [
              'Compute tract-level total energy cost as electricity + utility gas + other fuels.',
              'Aggregate within tract and income band by framework (AMI/FPL/LLSI/SMI).',
              'Allocate tract totals to place using TRCTPOPPCT weights, then sum to city totals.',
              'Compute burden as 100 * total_energy_cost / total_income.',
            ],
          })}
        />
      }
      onDownloadPNG={() => {
        const canvas = chartRef.current;
        if (!canvas) return;
        const link = document.createElement('a');
        link.href = canvas.toDataURL('image/png');
        link.download = `${formatFilename(location, 'Energy_Cost_Burden')}.png`;
        link.click();
      }}
      onDownloadCSV={() => {
        if (!rowsForTable.length) return;
        const header = ['framework', 'income_band', 'energy_burden_pct_income', 'year', 'geography_type', 'geofips'];
        const lines = [header.join(',')];
        for (const r of rowsForTable) {
          lines.push(
            [
              r.framework,
              r.income_band,
              r.energy_burden_pct_income ?? '',
              r.year,
              r.geography_type,
              r.geofips,
            ]
              .map((v) => `"${String(v).replace(/"/g, '""')}"`)
              .join(',')
          );
        }
        const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${formatFilename(location, 'Energy_Cost_Burden_Data')}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      }}
      pngFilename={formatFilename(location, 'Energy_Cost_Burden')}
      csvFilename={formatFilename(location, 'Energy_Cost_Burden_Data')}
    />
  );
};

export default LeadEnergyCostBurdenSection;
